import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  ContactInfo,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  SkillSection,
  PaperSection,
  CoreCompetenciesSection,
  SummarySection,
  ResumeSection,
  getPlaceholderClass
} from '../common/ResumeComponents';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText,
  isFieldUpdating
} from '../common/resumeHelpers';

// Template 2 - 심플하고 클린한 디자인
const Template2 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-2 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <h1 className={getPlaceholderClass(hasRealData.name, "text-3xl font-bold mb-2 text-blue-900")}>{currentData.name}</h1>
        <p className={getPlaceholderClass(hasRealData.title, "text-lg mb-2 text-gray-600")}>{currentData.title}</p>
        <ContactInfo
          phone={currentData.phone}
          email={currentData.email}
          address={currentData.address}
          socialLinks={currentData.socialLinks}
          hasRealData={hasRealData}
          isFieldUpdating={isFieldUpdating}
          updatingField={updatingField}
        />
      </div>

      <main className="p-6 space-y-6">
        {/* Summary & Key Strengths */}
        {(currentData.summary || currentData.keyStrengths.length > 0) && (
          <ResumeSection 
            title={titles.introduction}
            variant="bordered"
            titleClassName="text-xl font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4"
            showIcon={false}
          >
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm text-gray-700 mb-4"
            />
            {currentData.keyStrengths.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-gray-700 mb-3">{titles.coreCompetencies}</h3>
                <CoreCompetenciesSection
                  keyStrengths={currentData.keyStrengths}
                  hasRealData={hasRealData}
                  bulletStyle="•"
                  className="list-disc list-inside text-sm text-gray-700 ml-4"
                  itemClassName=""
                  textClassName="text-gray-700"
                />
              </div>
            )}
          </ResumeSection>
        )}

        {/* Work Experience */}
        {currentData.experiences.length > 0 && (
          <ResumeSection 
            title={titles.experience}
            variant="bordered"
            titleClassName="text-xl font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4"
            showIcon={false}
          >
            <ExperienceSection
              experiences={currentData.experiences}
              hasRealData={hasRealData}
              datePosition="right"
              positionClassName="text-lg font-semibold text-gray-800"
              companyClassName="text-lg font-semibold text-gray-800"
              dateClassName="text-sm text-gray-600"
              descriptionClassName="list-disc list-inside text-sm text-gray-700 ml-4"
              projectTitleClassName="text-sm font-semibold text-gray-700"
              projectDescriptionClassName="list-disc list-inside text-gray-600 ml-4 text-sm"
              projectIndent="mt-2 ml-4"
              bulletStyle="•"
            />
          </ResumeSection>
        )}

        {/* Education */}
        {currentData.education.length > 0 && (
          <ResumeSection 
            title={titles.education}
            variant="bordered"
            titleClassName="text-xl font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4"
            showIcon={false}
          >
            <EducationSection
              education={currentData.education}
              hasRealData={hasRealData}
              datePosition="right"
              degreeClassName="text-base font-semibold text-gray-800"
              schoolClassName="text-sm text-gray-700"
              majorClassName="text-xs text-gray-400"
              dateClassName="text-sm text-gray-600"
            />
          </ResumeSection>
        )}

        {/* Papers */}
        {hasRealData.papers && (
          <ResumeSection 
            title={titles.papers}
            variant="bordered"
            titleClassName="text-xl font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4"
            showIcon={false}
          >
            <PaperSection
              papers={currentData.papers}
              hasRealData={hasRealData}
              getPaperTypeText={getPaperTypeText}
              itemClassName="mb-3"
              titleClassName="text-base font-semibold text-gray-800"
              authorsClassName="text-sm text-gray-600"
              journalClassName="text-sm text-gray-700"
              typeClassName="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
            />
          </ResumeSection>
        )}

        {/* Certificates */}
        {currentData.certificates.length > 0 && (
          <ResumeSection 
            title={titles.certificates}
            variant="bordered"
            titleClassName="text-xl font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4"
            showIcon={false}
          >
            <div className="space-y-2">
              {currentData.certificates.map((cert, index) => (
                <div 
                  key={index} 
                  className="grid grid-cols-12 gap-2"
                >
                  <div className="col-span-5">
                    <h3 className={getPlaceholderClass(hasRealData.certificates, "text-sm font-semibold text-gray-800")}>
                      {cert.name}
                    </h3>
                  </div>
                  <div className="col-span-4">
                    <p className={getPlaceholderClass(hasRealData.certificates, "text-xs text-gray-600")}>
                      {cert.issuer}
                    </p>
                  </div>
                  <div className="col-span-3 text-right">
                    <p className={getPlaceholderClass(hasRealData.certificates, "text-xs text-gray-500")}>
                      {cert.date}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ResumeSection>
        )}

        {/* Skills */}
        {currentData.skills.length > 0 && (
          <ResumeSection 
            title={titles.skills}
            variant="bordered"
            titleClassName="text-xl font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4"
            showIcon={false}
          >
            <div className="grid grid-cols-2 gap-x-8 gap-y-1">
              {currentData.skills.map((skill, index) => (
                <div key={index} className="flex items-center">
                  <span className={getPlaceholderClass(hasRealData.skills, "text-sm text-gray-700")}>
                    • {skill}
                  </span>
                </div>
              ))}
            </div>
          </ResumeSection>
        )}
      </main>
    </div>
  );
};

export default Template2;